import { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '@hooks';
import CartServices from '@services/CartServices';
import { useAccount } from '@context/AccountContext';
import { useCart } from '@context/CartContext';
import { IconCircle } from '@components/styles/CommonStyles';
import { NewButton } from '@components/ui';
import { CartDeleteIcon, RemoveCartItemIcon } from '@components/icons';
import { H2, Text3 } from '@components/styles/Typography';

const customStyles = {
    overlay: {
        background: 'rgba(0, 0, 0, 0.7)',
        zIndex: '999',
    },
};

Modal.setAppElement('#__next');

const RemoveCartItem = ({ productID, name, modelID }) => {
    const { t, locale } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { basketHash } = useCart();
    const queryClient = useQueryClient();
    const { addToFavoritesMutation, currentUser } = useAccount();
    const openModal = () => {
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
    };

    const removeFromCartMutation = useMutation({
        mutationFn: CartServices.removeFromCart,
        onSuccess: () => {
            queryClient.refetchQueries(['getCart']);
            closeModal();
        },
    });

    const handleRemoveProduct = () => {
        removeFromCartMutation.mutate({
            basketHash,
            productID,
            token: currentUser,
        });
    };
    const removeAndAddFavorites = () => {
        handleRemoveProduct();
        addToFavoritesMutation.mutate({
            modelID,
            token: currentUser,
        });
    };

    return (
        <ModalWrapper>
            <IconCircle onClick={openModal}>
                <CartDeleteIcon width="15px" height="17px" />
            </IconCircle>
            <CartRemoveModal
                isOpen={isOpen}
                onRequestClose={closeModal}
                style={customStyles}
                contentLabel="Remove Cart Modal"
            >
                {currentUser ? (
                    <RemoveCartItemIcon />
                ) : (
                    <CartDeleteIcon width="50px" height="50px" />
                )}
                {currentUser ? (
                    <H2>{t('cartDeleteAndFav')}</H2>
                ) : (
                    <H2>{t('cartDelete')}</H2>
                )}
                {currentUser ? (
                    <TermsText>
                        {name[locale]} {t('cartDeleteAndFavText')}
                    </TermsText>
                ) : (
                    <TermsText>
                        {name[locale]} {t('cartDeleteText')}
                    </TermsText>
                )}
                <CartButtonsContainer>
                    <CartStyledRow size={currentUser ? '160px' : '100%'}>
                        <NewButton
                            type="secondary"
                            size="small"
                            content={t('cancel')}
                            handleClick={closeModal}
                        />
                        <NewButton
                            type="secondary"
                            size="small"
                            content={t('delete')}
                            handleClick={handleRemoveProduct}
                        />
                    </CartStyledRow>
                    {currentUser && (
                        <NewButton
                            type="primary"
                            size="small"
                            content={t('cartDeleteAndFav')}
                            handleClick={removeAndAddFavorites}
                            className="cart-delete-and-fav"
                        />
                    )}
                </CartButtonsContainer>
            </CartRemoveModal>
        </ModalWrapper>
    );
};

const ModalWrapper = styled.div`
    span {
        text-decoration: underline;
        cursor: pointer;
    }
`;

const CartButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
        .cart-delete-and-fav {
            width: 100%;
        }
    }
`;

const CartStyledRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: ${({ size }) => size && size};

    @media (max-width: 768px) {
        width: 100%;
        justify-content: space-between;
        margin: 0 0 20px 0;
    }
`;

const CartRemoveModal = styled(Modal)`
    position: fixed;
    inset: 0;
    margin: auto;
    width: 510px;
    height: fit-content;
    text-align: center;
    background: ${({ theme }) => theme.colors.secondary};
    padding: 20px;

    h2 {
        margin: 20px 0 10px;
    }
    p {
        margin: 0 0 20px 0;
        padding-bottom: 20px;
    }

    @media (max-width: 1024px) {
        width: calc(100% - 40px);
        h2 {
            margin: 0 0 10px;
        }
        p {
            margin: 0 0 20px 0;
            padding-bottom: 20px;
        }
        svg {
            width: 74px;
            height: 74px;
            margin-bottom: 8px;
        }
    }
`;

const TermsText = styled(Text3)`
    max-width: 474px;
    position: relative;
    padding: 0 25px 30px;
    ::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: ${({ theme }) => theme.colors.primary};
    }
    @media (max-width: 768px) {
        padding: 0;
    }
`;

export default RemoveCartItem;
